import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ActiveControls, EndReason, EndedControls, HeartbeatStatus, SortDirection,
} from './types';

const VALID_HEARTBEATS: readonly HeartbeatStatus[] = ['healthy', 'stale', 'abandoned', 'missing'];
const VALID_END_REASONS: readonly EndReason[] = ['teacher_ended', 'timeout', 'error'];
const VALID_ACTIVE_SORTS = ['started_at', 'teacher', 'program', 'slide_show'] as const;
const VALID_ENDED_SORTS = [
  'ended_at', 'started_at', 'duration', 'teacher', 'program', 'slide_show', 'end_reason',
] as const;
const VALID_WITHIN_HOURS = [1, 6, 24, 72, 168] as const;

const parseDir = (raw: string | null, fallback: SortDirection = 'desc'): SortDirection => (
  raw === 'asc' || raw === 'desc' ? raw : fallback
);

const parseSort = <K extends string>(raw: string | null, allowed: readonly K[], fallback: K): K => (
  raw && (allowed as readonly string[]).includes(raw) ? raw as K : fallback
);

const parseIntList = (params: URLSearchParams, key: string): number[] => (
  params.getAll(key).map(v => parseInt(v, 10)).filter(Number.isFinite)
);

const parseEnumList = <T extends string>(
  params: URLSearchParams, key: string, allowed: readonly T[],
): T[] => (
    params.getAll(key).filter((v): v is T => (allowed as readonly string[]).includes(v))
  );

export const DEFAULT_ACTIVE_CONTROLS: ActiveControls = {
  filters: { heartbeat: [], programIds: [] },
  page: 1,
  q: '',
  sort: { dir: 'desc', key: 'started_at' },
};

export const DEFAULT_ENDED_CONTROLS: EndedControls = {
  filters: { endReason: [], programIds: [], withinHours: 24 },
  page: 1,
  q: '',
  sort: { dir: 'desc', key: 'ended_at' },
};

const readSearchParams = (): URLSearchParams => {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
};

const writeSearchParams = (params: URLSearchParams) => {
  if (typeof window === 'undefined') return;
  const query = params.toString();
  const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
};

interface HydratedState {
  activeControls: ActiveControls;
  endedControls: EndedControls;
  tableMode: 'active' | 'ended';
}

export const hydrateFromUrl = (): HydratedState => {
  const params = readSearchParams();
  const tableMode = params.get('tab') === 'ended' ? 'ended' : 'active';

  if (tableMode === 'ended') {
    const withinRaw = parseInt(params.get('within_hours') ?? '', 10);
    const withinHours = (VALID_WITHIN_HOURS as readonly number[]).includes(withinRaw)
      ? withinRaw
      : 24;
    return {
      activeControls: DEFAULT_ACTIVE_CONTROLS,
      endedControls: {
        filters: {
          endReason: parseEnumList(params, 'end_reason', VALID_END_REASONS),
          programIds: parseIntList(params, 'program_ids'),
          withinHours,
        },
        page: Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1),
        q: params.get('q') ?? '',
        sort: {
          dir: parseDir(params.get('dir')),
          key: parseSort(params.get('sort'), VALID_ENDED_SORTS, 'ended_at'),
        },
      },
      tableMode,
    };
  }

  return {
    activeControls: {
      filters: {
        heartbeat: parseEnumList(params, 'heartbeat', VALID_HEARTBEATS),
        programIds: parseIntList(params, 'program_ids'),
      },
      page: Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1),
      q: params.get('q') ?? '',
      sort: {
        dir: parseDir(params.get('dir')),
        key: parseSort(params.get('sort'), VALID_ACTIVE_SORTS, 'started_at'),
      },
    },
    endedControls: DEFAULT_ENDED_CONTROLS,
    tableMode,
  };
};

const isDefaultActive = (c: ActiveControls): boolean => (
  c.q === ''
    && c.filters.heartbeat.length === 0
    && c.filters.programIds.length === 0
    && c.sort.key === DEFAULT_ACTIVE_CONTROLS.sort.key
    && c.sort.dir === DEFAULT_ACTIVE_CONTROLS.sort.dir
    && c.page === 1
);

const isDefaultEnded = (c: EndedControls): boolean => (
  c.q === ''
    && c.filters.endReason.length === 0
    && c.filters.programIds.length === 0
    && c.filters.withinHours === DEFAULT_ENDED_CONTROLS.filters.withinHours
    && c.sort.key === DEFAULT_ENDED_CONTROLS.sort.key
    && c.sort.dir === DEFAULT_ENDED_CONTROLS.sort.dir
    && c.page === 1
);

const serializeActive = (c: ActiveControls): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('tab', 'active');
  if (c.q) params.set('q', c.q);
  if (c.sort.key !== DEFAULT_ACTIVE_CONTROLS.sort.key) params.set('sort', c.sort.key);
  if (c.sort.dir !== DEFAULT_ACTIVE_CONTROLS.sort.dir) params.set('dir', c.sort.dir);
  c.filters.heartbeat.forEach(h => params.append('heartbeat', h));
  c.filters.programIds.forEach(id => params.append('program_ids', String(id)));
  if (c.page > 1) params.set('page', String(c.page));
  return params;
};

const serializeEnded = (c: EndedControls): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('tab', 'ended');
  if (c.q) params.set('q', c.q);
  if (c.sort.key !== DEFAULT_ENDED_CONTROLS.sort.key) params.set('sort', c.sort.key);
  if (c.sort.dir !== DEFAULT_ENDED_CONTROLS.sort.dir) params.set('dir', c.sort.dir);
  c.filters.endReason.forEach(r => params.append('end_reason', r));
  c.filters.programIds.forEach(id => params.append('program_ids', String(id)));
  if (c.filters.withinHours !== DEFAULT_ENDED_CONTROLS.filters.withinHours) {
    params.set('within_hours', String(c.filters.withinHours));
  }
  if (c.page > 1) params.set('page', String(c.page));
  return params;
};

interface UseTableControlsResult {
  activeControls: ActiveControls;
  endedControls: EndedControls;
  setActiveControls: (next: ActiveControls | ((prev: ActiveControls) => ActiveControls)) => void;
  setEndedControls: (next: EndedControls | ((prev: EndedControls) => EndedControls)) => void;
  setTableMode: (next: 'active' | 'ended') => void;
  tableMode: 'active' | 'ended';
}

// AIDEV-NOTE: Drives both tabs' state and mirrors it into the URL via history.replaceState.
// We're mounted outside a React Router context, so we can't use useSearchParams. Reading the URL
// at mount (hydrateFromUrl) lets sysadmins paste a filtered URL and land in the same view.
const useTableControls = (): UseTableControlsResult => {
  const initial = useRef<HydratedState>();
  if (!initial.current) initial.current = hydrateFromUrl();

  const [tableMode, setTableMode] = useState<'active' | 'ended'>(initial.current.tableMode);
  const [activeControls, setActiveControls] = useState<ActiveControls>(initial.current.activeControls);
  const [endedControls, setEndedControls] = useState<EndedControls>(initial.current.endedControls);

  useEffect(() => {
    const params = tableMode === 'active'
      ? serializeActive(activeControls)
      : serializeEnded(endedControls);
    const isDefault = tableMode === 'active'
      ? isDefaultActive(activeControls)
      : isDefaultEnded(endedControls);
    if (isDefault) {
      const stripped = new URLSearchParams();
      writeSearchParams(stripped);
    } else {
      writeSearchParams(params);
    }
  }, [tableMode, activeControls, endedControls]);

  const setTableModeStable = useCallback((next: 'active' | 'ended') => {
    setTableMode(next);
    if (next === 'active') setActiveControls(prev => ({ ...prev, page: 1 }));
    else setEndedControls(prev => ({ ...prev, page: 1 }));
  }, []);

  return {
    activeControls,
    endedControls,
    setActiveControls,
    setEndedControls,
    setTableMode: setTableModeStable,
    tableMode,
  };
};

export default useTableControls;
